import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import "@lunagoai/vue-ui/style.css";
import "./styles.css";
import { gcsVueUi } from "./ui/vueUi";
import AppShell from "./components/navigation/AppShell.vue";
import RootApp from "./RootApp.vue";
import { clearSession, ensureSession, isAdministrator, isSuperAdministrator } from "./app/session";

const router = createRouter({ history: createWebHistory(), routes: [
  { path: "/", redirect: "/app/dashboard" },
  { path: "/login", component: () => import("./pages/LoginPage.vue") },
  { path: "/forbidden", component: () => import("./pages/ForbiddenPage.vue") },
  {
    path: "/app",
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      { path: "dashboard", component: () => import("./pages/app/CustomerDashboardPage.vue") },
      { path: "reports", component: () => import("./pages/app/ReportHistoryPage.vue") },
      { path: "reports/new", component: () => import("./pages/app/CreateReportPage.vue") },
      { path: "reports/:jobId", component: () => import("./pages/app/ReportDetailPage.vue") },
      { path: "tests/one-off", component: () => import("./pages/app/OneOffTestPage.vue") },
      { path: "account", component: () => import("./pages/app/AccountPage.vue") },
    ],
  },
  {
    path: "/admin",
    component: AppShell,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: "overview", component: () => import("./pages/admin/AdminOverviewPage.vue") },
      // Reports are the single user-facing report workspace. Keep the old
      // admin URLs as compatibility redirects for bookmarks and old links.
      { path: "jobs", redirect: "/app/reports" },
      { path: "jobs/:jobId", redirect: (to) => `/app/reports/${String(to.params.jobId)}` },
      { path: "flow", component: () => import("./pages/admin/AdminFlowPage.vue") },
      { path: "sources", component: () => import("./pages/admin/AdminSourcesPage.vue") },
      { path: "sources/runs/:runId", component: () => import("./pages/admin/AdminSourceRunPage.vue") },
      { path: "snapshots/:snapshotId", component: () => import("./pages/admin/AdminSnapshotPage.vue") },
      { path: "schedules", component: () => import("./pages/admin/AdminSchedulesPage.vue") },
      { path: "schedule-runs", component: () => import("./pages/admin/AdminScheduleRunsPage.vue") },
      { path: "prompt", redirect: "/admin/flow" },
      { path: "models", component: () => import("./pages/admin/AdminModelsPage.vue"), meta: { requiresSuperadmin: true } },
      { path: "usage", component: () => import("./pages/admin/AdminUsagePage.vue") },
      { path: "users", component: () => import("./pages/admin/AdminUsersPage.vue") },
      { path: "audit", component: () => import("./pages/admin/AdminAuditPage.vue"), meta: { requiresSuperadmin: true } },
    ],
  },
  { path: "/demo", component: () => import("./DemoApp.vue") },
  { path: "/reports/:jobId", component: () => import("./ReportViewer.vue"), meta: { requiresAuth: true } },
  { path: "/:pathMatch(.*)*", redirect: "/app/dashboard" },
] });

const preloadRecoveryKey = "gcs-vite-preload-recovery";
let preloadRecoveryAttempted = false;

if (typeof window !== "undefined") {
  let sessionExpiryRedirecting = false;
  window.addEventListener("gcs:session-expired", () => {
    if (sessionExpiryRedirecting || window.location.pathname === "/login") return;
    sessionExpiryRedirecting = true;
    clearSession();
    const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    void router.replace({ path: "/login", query: { redirect } }).finally(() => { sessionExpiryRedirecting = false; });
  });

  try {
    preloadRecoveryAttempted = window.sessionStorage.getItem(preloadRecoveryKey) === "1";
    window.sessionStorage.removeItem(preloadRecoveryKey);
  } catch {
    // Session storage can be unavailable in strict privacy modes.
  }

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    if (preloadRecoveryAttempted) return;
    try {
      window.sessionStorage.setItem(preloadRecoveryKey, "1");
    } catch {
      // The normal browser reload remains the last-resort recovery path.
    }
    window.location.reload();
  }, { once: true });
}

// Artifact URLs are frequently opened from email, bookmarks, or shared review
// links. Check the session before mounting ReportViewer so an anonymous visitor
// reaches the login UI without first issuing a protected artifact request.
router.beforeEach(async (to) => {
  if (to.path === "/demo") return true;

  const session = await ensureSession().catch(() => {
    clearSession();
    return null;
  });

  if (to.path === "/login") {
    return session?.authenticated ? { path: "/app/dashboard" } : true;
  }

  if (!to.meta.requiresAuth) return true;
  if (!session?.authenticated) return { path: "/login", query: { redirect: to.fullPath } };
  if (to.meta.requiresAdmin && !isAdministrator()) return { path: "/forbidden" };
  if (to.meta.requiresSuperadmin && !isSuperAdministrator()) return { path: "/forbidden" };
  return true;
});

createApp(RootApp).use(gcsVueUi).use(router).mount("#app");
